import React from 'react';
import ProgressStatusBadge from '../../../Utils/ProgressStatusBadge/ProgressStatusBadge';
import { mapStatusToKey } from '../../../../Helpers/mapStatusToKey';
import styles from './ObjectiveDetailsCard.module.css';
import { ImprovementDetail } from '../../../../Types/Improve/ImprovementPlanDetail';
import Button from '../../../Utils/Button/Button';
import { ToggleImprovementArchived } from '../../../../API/Improve/Improve';
import { toast } from 'react-toastify';
import CommentsIcon from '../../../Icons/CommentsIcon/CommentsIcon';
import Tooltip from '../../../Utils/Tooltip/Tooltip';
import ConfirmAction from '../../../Utils/ConfirmAction/ConfirmAction';
import LinkIcon from '../../../Icons/LinkIcon';

type ObjectiveDetailsCardProps = {
  objective: ImprovementDetail;
  isActive?: boolean;
  onClickHandler?: () => void;
  archived?: boolean;
  onArchiveToggle?: () => void;
  setEvidenceVisible: (visible: boolean, objectiveId?: string) => void;
  setCommentsVisible: (visible: boolean) => void;
  setLinkedDiagnosticsVisible: (visible: boolean, objectiveId?: string) => void;
  canArchive?: boolean;
  roleName?: string;
  editMode?: boolean;
  onDelete?: (objectiveId: string) => void;
};

const ObjectiveDetailsCard = ({
  objective,
  isActive,
  onClickHandler,
  archived = false,
  onArchiveToggle,
  setEvidenceVisible,
  setCommentsVisible,
  setLinkedDiagnosticsVisible,
  canArchive,
  roleName,
  editMode,
  onDelete
}: ObjectiveDetailsCardProps) => {
  const handleArchiveClick = async () => {
    console.log('Archiving objective with ID:', objective.id, 'Title:', objective.title);
    try {
      await ToggleImprovementArchived(objective.id);
      toast.success(`${objective.title} has been successfully ${archived ? 'unarchived' : 'archived'}.`);
      onArchiveToggle?.();
    } catch (error) {
      console.error(error);
      toast.error(`Failed to ${archived ? 'unarchive' : 'archive'} objective. Error: ${error}`);
    }
  };

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div className={styles.card + (isActive ? ` ${styles.active}` : '')}>
      <div onClick={onClickHandler} style={{ cursor: 'pointer', flex: 1 }} className={styles.row}>
        <h3>{objective.title}</h3>
        <div className={styles.row}>
          <ProgressStatusBadge status={mapStatusToKey(objective.status)} text={objective.status ?? 'Status not set'} />
          <ProgressStatusBadge 
            status={mapStatusToKey(objective.actualImpact)} 
            text={objective.actualImpact ? String(objective.actualImpact).replace(/_/g, ' ') : 'Impact not set'} 
          />

          <span onClick={stop}  style={{ display: 'flex', gap: '0.5rem' }}>
            <Tooltip text={`View linked diagnostics`} align={'top'} sideAlign='left' disabled={false}>
              <Button clickHandler={() => setLinkedDiagnosticsVisible(true, objective.id)} className={styles.commentsButton} colour={"transparent"}>
                <LinkIcon className={styles.linkIcon} size={18} />
              </Button>
            </Tooltip>
            <Button clickHandler={() => setCommentsVisible(true)} className={styles.commentsButton} colour={"transparent"}>
              <CommentsIcon className={styles.commentsIcon} size={18} />
            </Button>
          </span>
        </div>
      </div>

      <div className={styles.column}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', alignSelf: 'flex-end' }}>
          <span onClick={stop}>
            <Button
              size="Small"
              colour="#FFF"
              textColour="#000"
              border="rgba(0,0,0,0.2)"
              clickHandler={() => setEvidenceVisible(true, objective.id)}
            >
              View Linked Evidence
            </Button>
          </span>

          <Tooltip text={`Your role as ${roleName} means that you cannot archive objectives`} align={'top'} sideAlign='left' disabled={canArchive}>
            <span onClick={stop}>
              <Button
                size="Small"
                colour="#FFF"
                textColour="#000"
                border="rgba(0,0,0,0.2)"
                clickHandler={handleArchiveClick}
                disabled={!canArchive}
              >
                {archived ? 'Unarchive' : 'Archive'}
              </Button>
            </span>
          </Tooltip>

          {editMode && onDelete &&
              <Button
                clickHandler={() => onDelete(objective.id)}
                colour={'rgb(240, 223, 222)'}
                textColour={"black"}
              >
                <img src="/img/trash-red.svg" alt="Delete Objective" style={{ width: '16px', height: '16px' }} /> 
              </Button>
              }
          
        </div>
      </div>
    </div>
  );
};

export default ObjectiveDetailsCard;
